import React, { useMemo, useCallback, useState } from 'react';
import { Typography, Card, CardContent, Switch, Tooltip, Box, Chip } from '@mui/material';
import { Percent } from '@mui/icons-material';
import { ResponsiveLine } from '@nivo/line';
import { useTheme } from '@mui/material/styles';
import { formatCostValue } from '../../../../utils/formatNumbers';
import { format } from 'date-fns';
import { getChartData, generateCompleteTimeSeries, periodToDateRange, comparePeriods, getAverageFromTimeSeries, getStandardDeviationFromTimeSeries } from './useChartData';
import { CHART_VIEWS } from './ItemAnalytics';

function mergeDataPoints(realDataPoints, projectedDataPoints, period, lastNonZeroUsagePeriod) {
    // Convert to chart format - combine and deduplicate by period
    const allDataMap = new Map();
    
    // Add projected data first
    projectedDataPoints.forEach(point => {
        allDataMap.set(point.x, { ...point, isProjected: true });
    });
    
    // Add real data second (will overwrite any duplicates with real data)
    realDataPoints.forEach(point => {
        const hasProjectedPoint = allDataMap.has(point.x);
        const isAfterLastNonZero = lastNonZeroUsagePeriod ? comparePeriods(point.x, lastNonZeroUsagePeriod, period) > 0 : false;

        if (hasProjectedPoint && !isAfterLastNonZero || !hasProjectedPoint) {
            allDataMap.set(point.x, { ...point, isProjected: false });
        }
    });
    
    // Convert back to array and sort chronologically
    return Array.from(allDataMap.values()).sort((a, b) => {
        return comparePeriods(a.x, b.x, period);
    });
}

function getSDMultiplier(projectedIndex) {
    if (projectedIndex === 0) {
        return 0.1;
    } else if (projectedIndex === 1) {
        return 0.2;
    } else if (projectedIndex === 2) {
        return 0.3;
    } else if (projectedIndex === 3) {
        return 0.4;
    } else if (projectedIndex === 4) {
        return 0.6;
    } else if (projectedIndex === 5) {
        return 0.9;
    } else if (projectedIndex === 6) {
        return 1.2;
    } else if (projectedIndex === 7) {
        return 1.4;
    } else if (projectedIndex === 8) {
        return 1.6;
    } else if (projectedIndex === 9) {
        return 1.8;
    } else if (projectedIndex === 10) {
        return 1.9;
    } else {
        return 2.0; // Final should be 2 standard deviations from the mean
    }
}

function UsageChartForecast({ timeSeriesData, period, forecastDateRange, selectedPeriod, setSelectedPeriod }) {
    const theme = useTheme();
    const [normalizeByCase, setNormalizeByCase] = useState(false);
    
    // Handle point selection
    const handlePointClick = useCallback((point) => {
        const periodId = point.data.x;
        setSelectedPeriod(selectedPeriod === periodId ? null : periodId);
    }, [selectedPeriod, setSelectedPeriod]);

    const realDataDateRange = useMemo(() => {
        return {
            ...forecastDateRange,
            endDate: format(new Date(), 'yyyy-MM-dd')
        };
    }, [forecastDateRange]);

    // Process data to create forecast with projections
    const forecastData = useMemo(() => {
        if (!timeSeriesData) {
            return [];
        }

        const realCompleteData = generateCompleteTimeSeries(timeSeriesData, period, realDataDateRange.startDate, realDataDateRange.endDate);

        // Find the last date with non-zero used_quantity
        const lastNonZeroUsagePeriod = [...realCompleteData].sort((a, b) => comparePeriods(a.period, b.period, period))
            .filter(point => point && point.used_quantity > 0)
            .pop()?.period || null;

        const lastNonZeroUsageDate = lastNonZeroUsagePeriod ? 
            periodToDateRange(lastNonZeroUsagePeriod, period)?.endDate || realDataDateRange.endDate 
            : realDataDateRange.endDate;

        // Filter real data up to transition point for average calculation
        const filteredRealData = realCompleteData.filter(point => {
            if (!point || typeof point.used_quantity !== 'number') return false;
            if (!lastNonZeroUsagePeriod) return true;
            return comparePeriods(point.period, lastNonZeroUsagePeriod, period) <= 0;
        });

        // Calculate average and standard deviation - normalize by case if toggle is active
        const normalizedFilteredData = normalizeByCase ? 
            filteredRealData.map(point => ({
                ...point,
                used_quantity: point.num_cases > 0 ? point.used_quantity / point.num_cases : 0
            })) : 
            filteredRealData;

        const average = getAverageFromTimeSeries(normalizedFilteredData, 'used_quantity');
        
        const standardDeviation = getStandardDeviationFromTimeSeries(normalizedFilteredData, 'used_quantity', average);

        // Generate projected data points based on average
        const projectedCompleteData = generateCompleteTimeSeries(
            realCompleteData, 
            period, 
            lastNonZeroUsageDate, 
            forecastDateRange.endDate,
            average, // Convert back to quantity for projections
            true // Mark as projection
        );

        const realChartData = getChartData(realCompleteData, CHART_VIEWS.FORECAST_VIEW) || [];
        const projectedChartData = getChartData(projectedCompleteData, CHART_VIEWS.FORECAST_VIEW) || [];
        
        // Extract data arrays from the series objects and combine them
        const realDataPoints = realChartData.find(series => series.id === 'total_used')?.data || [];
        const projectedDataPoints = projectedChartData.find(series => series.id === 'total_used')?.data || [];
        
        // Merge and sort data points
        const allDataPoints = mergeDataPoints(realDataPoints, projectedDataPoints, period, lastNonZeroUsagePeriod);
        
        // Create uncertainty bounds for projected data using standard deviation
        let projectedIndex = 0;
        const upperBoundPoints = allDataPoints.map(point => {
            if (!point.isProjected) {
                return { ...point };
            }
            
            // Determine SD multiplier based on projection position
            const sdMultiplier = getSDMultiplier(projectedIndex);
            projectedIndex++;
            
            return {
                x: point.x,
                y: point.y + (standardDeviation * sdMultiplier),
                isProjected: point.isProjected
            };
        });
        
        // Reset index for lower bounds
        projectedIndex = 0;
        const lowerBoundPoints = allDataPoints.map(point => {
            if (!point.isProjected) {
                return { ...point };
            }
            
            // Determine SD multiplier based on projection position
            const sdMultiplier = getSDMultiplier(projectedIndex);
            projectedIndex++;
            
            return {
                x: point.x,
                y: Math.max(0.01, point.y - (standardDeviation * sdMultiplier)),
                isProjected: point.isProjected
            };
        });

        return [
            {
                id: 'total_used',
                color: theme.palette.analytics.cardTypes.used,
                data: allDataPoints.map(point => {
                    const normalizedY = normalizeByCase && point.cases > 0 ? point.y / point.cases : point.y;
                    return { ...point, y: normalizedY, originalY: point.y };
                })
            },
            {
                id: 'upper_bound',
                color: theme.palette.analytics.forecast,
                data: upperBoundPoints.map(point => {
                    const normalizedY = normalizeByCase && point.cases > 0 ? point.y / point.cases : point.y;
                    return { ...point, y: normalizedY, originalY: point.y };
                })
            },
            {
                id: 'lower_bound',
                color: theme.palette.analytics.forecast,
                data: lowerBoundPoints.map(point => {
                    const normalizedY = normalizeByCase && point.cases > 0 ? point.y / point.cases : point.y;
                    return { ...point, y: normalizedY, originalY: point.y };
                })
            }
        ];
    }, [timeSeriesData, period, realDataDateRange, forecastDateRange, normalizeByCase]);

    // Calculate dynamic min/max for chart based on data and standard deviation
    const chartYScale = useMemo(() => {
        if (!forecastData || forecastData.length === 0) {
            return { type: 'linear', min: 0, max: 'auto', stacked: false, reverse: false };
        }

        // Extract all y values from all series (including bounds)
        const usedSeries = forecastData.find(s => s.id === 'total_used');
        const lowerBoundSeries = forecastData.find(s => s.id === 'lower_bound');
        
        if (!usedSeries) {
            return { type: 'linear', min: 0, max: 'auto', stacked: false, reverse: false };
        }

        const usedYValues = usedSeries.data
            .map(point => point.y)
            .filter(y => typeof y === 'number' && !isNaN(y));

        const lowerBoundYValues = lowerBoundSeries?.data
            .map(point => point.y)
            .filter(y => typeof y === 'number' && !isNaN(y)) || [];

        if (usedYValues.length === 0) {
            return { type: 'linear', min: 0, max: 'auto', stacked: false, reverse: false };
        }

        // Calculate min value from main usage series
        const minValue = Math.min(...usedYValues);

        // Calculate standard deviation
        const calculateSD = (values) => {
            if (values.length <= 1) return 0;
            const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
            const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / (values.length - 1);
            return Math.sqrt(variance);
        };

        const standardDeviation = calculateSD(usedYValues);

        // Calculate adjusted min (don't go below 0)
        let adjustedMin = Math.max(0, minValue - 0.5 * standardDeviation);
        
        // Check if lower bound minimum is lower than adjustedMin
        if (lowerBoundYValues.length > 0) {
            const lowerBoundMin = Math.min(...lowerBoundYValues);
            adjustedMin = Math.min(adjustedMin, Math.max(0, lowerBoundMin));
        }

        return {
            type: 'linear',
            min: adjustedMin,
            max: 'auto',
            stacked: false,
            reverse: false
        };
    }, [forecastData]);

    // Find the transition point for the vertical line (first projected data point)
    const transitionPoint = useMemo(() => {
        if (!forecastData || forecastData.length === 0) return null;
        
        const usedSeries = forecastData.find(s => s.id === 'total_used');
        if (!usedSeries || !usedSeries.data) return null;
        
        // Find the first point that IS projected
        const projectedDataPoints = usedSeries.data.filter(point => point.isProjected);
        if (projectedDataPoints.length === 0) return null;
        
        // Sort projected data points chronologically and get the first one
        const sortedProjectedPoints = projectedDataPoints.sort((a, b) => {
            return comparePeriods(a.x, b.x, period);
        });
        
        return sortedProjectedPoints[0]?.x || null;
    }, [forecastData, period]);

    // Memoized format period label for x-axis
    const formatPeriodLabel = useCallback((periodStr) => {
        if (period === 'week') {
            return `Week ${periodStr.split('-W')[1] || periodStr}`;
        }
        return periodStr;
    }, [period]);

    // Custom layer for vertical line at transition point
    const VerticalLineLayer = useCallback(({ xScale, innerHeight }) => {
        if (!transitionPoint) return null;
        
        const x = xScale(transitionPoint);
        if (typeof x !== 'number') return null;

        return (
            <g>
                <line
                    x1={x}
                    x2={x}
                    y1={0}
                    y2={innerHeight}
                    stroke={theme.palette.warning.main}
                    strokeWidth={2}
                    strokeDasharray="5,5"
                    opacity={0.8}
                />
                <text
                    x={x + 8}
                    y={20}
                    fill={theme.palette.warning.main}
                    fontSize={12}
                    fontWeight={500}
                >
                    Projection Start
                </text>
            </g>
        );
    }, [transitionPoint, theme.palette.warning.main]);

    // Custom tooltip
    const CustomTooltip = useMemo(() => {
        const tooltipStyles = {
            container: {
                padding: 12,
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 4,
                boxShadow: theme.shadows[4],
                minWidth: 200,
                maxWidth: 300
            },
            title: {
                marginBottom: 8
            },
            valueRow: {
                display: 'flex',
                alignItems: 'center',
                marginBottom: 4
            },
            colorBox: {
                width: 12,
                height: 12,
                marginRight: 8,
                borderRadius: 2
            },
            valueText: {
                textTransform: 'capitalize'
            },
            projectedText: {
                color: theme.palette.warning.main,
                fontSize: '0.875rem',
                fontStyle: 'italic'
            }
        };

        const TooltipComponent = ({ point }) => {
            const currentValue = point.data.y > .01 ? point.data.y : 0;
            const isProjected = point.data.isProjected || false;
            
            const formattedValue = `${currentValue}`;

            // Don't show tooltip for bounds series
            if (point.serieId === 'upper_bound' || point.serieId === 'lower_bound') {
                return null;
            }

            return (
                <div style={tooltipStyles.container}>
                    <div style={tooltipStyles.title}>
                        <strong>{formatPeriodLabel(point.data.x)}</strong>
                    </div>
                    <div style={tooltipStyles.valueRow}>
                        <div
                            style={{
                                ...tooltipStyles.colorBox,
                                background: point.serieColor
                            }}
                        />
                        <span style={tooltipStyles.valueText}>
                            Used: {formattedValue}
                        </span>
                    </div>
                    {!isProjected && (
                        <div style={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                            Cases: {point.data.cases}
                        </div>
                    )}
                </div>
            );
        };

        TooltipComponent.displayName = 'ForecastTooltip';
        return TooltipComponent;
    }, [theme, formatPeriodLabel]);

    if (!timeSeriesData || !forecastData.length) {
        return (
            <Card>
                <CardContent>
                    <Typography color="text.secondary" align="center">
                        No usage data available for forecast
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <div>
                        <Typography variant="h6" gutterBottom>
                            {'Usage forecast in quantity over time'}
                            {normalizeByCase ? ' (per case)' : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Historical data with 3-month projection based on average daily usage. 
                            Dashed line indicates start of projections.
                        </Typography>
                    </div>
                    <Tooltip title="Normalize per case">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Percent sx={{ fontSize: '1rem', opacity: normalizeByCase ? 1 : 0.5, mr: 0.5 }} />
                            <Switch 
                                checked={normalizeByCase}
                                onChange={(e) => setNormalizeByCase(e.target.checked)}
                                size="small"
                            />
                        </Box>
                    </Tooltip>
                </Box>
                <div style={{ height: 400, marginTop: 36 }}>
                    <ResponsiveLine
                        key={`forecast-${timeSeriesData?.length || 0}-${period}`}
                        data={forecastData}
                        colors={(serie) => serie.color}
                        curve={'monotoneX'}
                        margin={{ top: 50, right: 130, bottom: 110, left: 80 }}
                        xScale={{ type: 'point' }}
                        yScale={chartYScale}
                        yFormat=" >-.2f"
                        axisTop={null}
                        axisRight={null}
                        onClick={handlePointClick}
                        axisBottom={{
                            tickSize: 5,
                            tickPadding: 12,
                            tickRotation: -45,
                            legend: 'Time Period',
                            legendOffset: 80,
                            legendPosition: 'middle',
                            format: formatPeriodLabel
                        }}
                        axisLeft={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: normalizeByCase ? 'Quantity per Case' : 'Quantity',
                            legendOffset: -60,
                            legendPosition: 'middle'
                        }}
                        enableArea={false}
                        useMesh={true}
                        layers={[
                            'grid',
                            'markers',
                            'axes',
                            // Area layer for uncertainty bounds
                            ({ series, xScale, yScale }) => {
                                const usedSeries = series.find(s => s.id === 'total_used');
                                const upperSeries = series.find(s => s.id === 'upper_bound');
                                const lowerSeries = series.find(s => s.id === 'lower_bound');
                                
                                if (!usedSeries || !upperSeries || !lowerSeries) return null;

                                // Create path for uncertainty area (only for projected points)
                                const projectedPoints = usedSeries.data.filter(d => d.data.isProjected);
                                if (projectedPoints.length === 0) return null;

                                const upperPath = projectedPoints.map(d => {
                                    const upperPoint = upperSeries.data.find(ud => ud.data.x === d.data.x);
                                    return [xScale(d.data.x), yScale(upperPoint?.data?.y || d.data.y)];
                                });

                                const lowerPath = projectedPoints.map(d => {
                                    const lowerPoint = lowerSeries.data.find(ld => ld.data.x === d.data.x);
                                    return [xScale(d.data.x), yScale(lowerPoint?.data?.y || d.data.y)];
                                }).reverse();

                                const pathData = [...upperPath, ...lowerPath].map(([x, y], i) => 
                                    `${i === 0 ? 'M' : 'L'} ${x} ${y}`
                                ).join(' ') + ' Z';

                                return (
                                    <path
                                        d={pathData}
                                        fill={theme.palette.analytics.forecast}
                                        stroke="none"
                                    />
                                );
                            },
                            'areas',
                            'lines',
                            'points',
                            'slices',
                            'mesh',
                            VerticalLineLayer,
                            'legends'
                        ]}
                        legends={[
                            {
                                anchor: 'bottom-right',
                                direction: 'column',
                                justify: false,
                                translateX: 120,
                                translateY: 0,
                                itemsSpacing: 0,
                                itemDirection: 'left-to-right',
                                itemWidth: 100,
                                itemHeight: 20,
                                itemOpacity: 0.85,
                                symbolSize: 12,
                                symbolShape: 'circle',
                                symbolBorderColor: theme.palette.action.disabled,
                                data: [
                                    {
                                        id: 'total_used',
                                        label: 'Usage',
                                        color: forecastData.find(s => s.id === 'total_used')?.color
                                    },
                                    {
                                        id: 'projection',
                                        label: 'Uncertainty',
                                        color: forecastData.find(s => s.id === 'upper_bound')?.color
                                    }
                                ],
                                effects: [
                                    {
                                        on: 'hover',
                                        style: {
                                            itemBackground: 'rgba(0, 0, 0, .03)',
                                            itemOpacity: 1
                                        }
                                    }
                                ]
                            }
                        ]}
                        tooltip={CustomTooltip}
                        theme={{
                            background: 'transparent',
                            text: {
                                fill: theme.palette.text.primary
                            },
                            axis: {
                                ticks: {
                                    line: {
                                        stroke: theme.palette.divider
                                    },
                                    text: {
                                        fill: theme.palette.text.primary
                                    }
                                },
                                legend: {
                                    text: {
                                        fill: theme.palette.text.primary
                                    }
                                }
                            },
                            grid: {
                                line: {
                                    stroke: theme.palette.divider,
                                    strokeWidth: 1
                                }
                            }
                        }}
                    />
                </div>
                {selectedPeriod && (
                    <Box sx={{ mt: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                            Selected Period: {formatPeriodLabel(selectedPeriod)}
                        </Typography>
                        <Chip 
                            label="Clear Selection" 
                            size="small" 
                            variant="outlined"
                            onClick={() => setSelectedPeriod(null)}
                            sx={{ ml: 2 }}
                        />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

export default UsageChartForecast;